import { useMemo, useRef } from "react";
import { type SrpProductData } from "~/utils/models";

import { Trans, useTranslation } from "react-i18next";
import { SRPPackageRestrictionContext } from "~/features/PackageRestriction/SRPPackageRestrictionContext";
import {
  calculateSalesCampaign4CampaignPage,
  useSelectPricingDetails,
} from "~/features/salesCampaign/utils/salesCampaignUtils";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";

import { Price as PriceV3, type PriceProps } from "~/domains/pricing/components/Price/v3/Price";
import { SalesDataProvider, useSalesData } from "~/features/salesCampaign/hooks/useSalesData";
import {
  ProductContext,
  ProductDescriptorContext,
  useProductDescriptorContext,
} from "~/domains/product/ProductContext";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  styled,
  Typography,
} from "~/common/mui";
import { useResultTileZoovuParams } from "~/features/search/utils/searchUtils";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { Product } from "~/domains/product/Product";
import { Parameters, ProductParams2 } from "~/domains/product/components/ProductParamsV2";

import { getFeaturesAsParams } from "./getFeatureData";

import { type UnitMode } from "~/domains/userPreferences/types/UnitMode";
import { constructAvailability } from "./constructAvailability";

import {
  isProductComparisonCheckboxVisible,
  ProductComparisonCheckbox,
} from "~/features/compareProducts/components/ProductComparisonCheckbox";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import { getGALocationForSearchItem } from "~/features/search/utils/searchPageUtils";
import { useSearchPageType } from "~/features/search/context/SearchPageTypeContext";
import { isNonNull } from "~/common/utils/reducers";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { getListPrice, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { PriceElements } from "~/domains/pricing/components/Price/PriceV4";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import StockIndicator from "~/domains/product/components/StockIndicator/StockIndicator";
import { useAddProductToWishlistFlow } from "~/features/wishlist/hooks/useAddProductToWishlistFlow";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import { productIsLinear, renderButtonsNew } from "../useSrpButtons";
import { userIsCustomer } from "~/domains/user/utils/roles";
import {
  C_INFORMATION_CONTAINER,
  C_ON_INFORMATION_CONTAINER,
  C_ON_SURFACE,
  C_SURFACE,
} from "~/common/components/icon/Colors";
import MMDAssistant from "~/features/productDetails/components/MMDAssistant/MMDAssistant";
import type { ProductData } from "~/domains/product/types/ProductData";
import { LinkType } from "~/common/components/Link";
import { withDefaults } from "~/common/hoc/withDefaults";
import { useSrpCmnLogic } from "~/features/productDetails/components/MMDAssistant/logic/useSrpCmnLogic";
import classNames from "classnames";
import { useProductHelper } from "~/domains/product/utils/ProductHelper";
import { SaleQuantityIndicator } from "~/domains/salesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";
import { SaleEndIndicator } from "~/domains/salesCampaign/components/SaleEndIndicator/SaleEndIndicator";
import { SaleDiscountIndicator } from "~/domains/salesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import { useUnitMode } from "~/domains/userPreferences/hooks/useUnitMode";
import { Parameter, ParameterStack } from "../../../../../../../../common/components/ParameterV2";
import { CURRENT_LANGUAGE, INVALID_PACKAGE_CODE } from "~/utils/constants";
import { ReconditionableTag } from "~/domains/product/components/ReconditionableTag";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import { getInStock } from "~/utils/packageEntryUtils";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { isSiteRegion } from "~/utils/siteModes";
import { CountryCode } from "~/utils/countryData";
import { ZoovuZoe, type ZoovuZoeRef } from "~/domains/zoe/ZoovuZoe";
import { useScript } from "~/common/hooks/useScript";
import { SearchPageType } from "~/features/search/models/SearchPageType";

interface Props {
  // @deprecated - this property is not used since phase in out flag feature was removed
  isPdp?: boolean;
  productCode: string;
  index: number;
  onProductClick?: (area: string, product: SrpProductData) => void;
  searchQuery: string;
  className?: string;
  isTechnicalView?: boolean;
}

export const SearchResultListItemNew = ({
  isTechnicalView,
  productCode,
  onProductClick,
  isPdp,
  index,
  searchQuery,
  className,
}: Props) => {
  const originalProductDescriptor = ProductService.useProductDescriptor({
    productCode,
    priceReference: getInitialPriceReference(),
    options: { fetch: true, fetchAvailability: true },
  });

  const productDescriptor = useMemo(
    () => constructAvailability(originalProductDescriptor),
    [originalProductDescriptor],
  );

  const mmdCmnData = useSrpCmnLogic(productDescriptor.base);

  const stock =
    productDescriptor.availability?.packagingInformation.reduce((acc, pack) => getInStock(pack) + acc, 0) ?? 0;
  const inStock = stock > 0;

  return (
    <ItemRoot
      className={classNames("jsSearchResultProduct", className)}
      data-test-id={`search-result-item`}
      {...useResultTileZoovuParams(productDescriptor.base)}
    >
      {NewVersionMessage.isVisible(productDescriptor.base) && (
        <NewVersionMessageSearch inStock={inStock} successorProduct={productDescriptor.base.successorProduct} />
      )}
      <SearchResultListItemContentNew
        productCode={productCode}
        onProductClick={onProductClick}
        isPdp={isPdp}
        index={index}
        searchQuery={searchQuery}
        isTechnicalView={isTechnicalView}
      />
      {productDescriptor.base.displayMMDAssistant && (
        <MMDMessage
          marketDesignation={productDescriptor.base.marketDesignation}
          initialData={{
            quantity: 1,
            priceReference: productDescriptor.priceReference,
            ...mmdCmnData,
          }}
        />
      )}
    </ItemRoot>
  );
};

const MMDMessage = styled(
  withDefaults(MMDAssistant, {
    variant: "outlined",
    elevation: 0,
    isPDP: true,
  }),
)(({ theme }) => ({
  border: "none",
  borderRadius: 0,
  paddingBlock: theme.spacing(1),
  paddingInline: theme.spacing(2),
}));

export const SearchResultListItemContentNew = ({ productCode, isPdp, className, isTechnicalView }: Props) => {
  const { t } = useTranslation();
  const zoovuRef = useRef<ZoovuZoeRef>(null);
  const pageSize = useNumericScreenSize();

  const originalProductDescriptor = ProductService.useProductDescriptor({
    productCode,
    priceReference: getInitialPriceReference(),
  });

  const productDescriptor = useMemo(
    () => constructAvailability(originalProductDescriptor),
    [originalProductDescriptor],
  );
  const productHelper = useProductHelper(productDescriptor);

  const pageType = useSearchPageType();
  const gaLocation = getGALocationForSearchItem(isPdp, pageType);

  const unitMode = useUnitMode();

  const canSeePrice =
    userHasPermission(Permission.PRICE) &&
    (!productDescriptor.base.externalProduct ||
      (productDescriptor.base.externalProduct && !isProductPMA(productDescriptor)));
  const canSeeStock =
    userHasPermission(Permission.AVAILABILITY) &&
    (!productDescriptor.base.externalProduct ||
      (productDescriptor.base.externalProduct && !isProductPMA(productDescriptor)));
  const showHorizontalDivider = pageSize < useNumericScreenSize.tablet && (canSeePrice || canSeeStock);
  const showVerticalDivider = pageSize >= useNumericScreenSize.tablet;

  const firstSalesCampaignObj = Object.values(productDescriptor.base.salesCampaign ?? {})?.[0]?.[0];
  const salesCampaignCode = firstSalesCampaignObj?.code;
  const { status: salesStatus, data } = useMemo(
    () => calculateSalesCampaign4CampaignPage({ productDescriptor, salesCampaignCode }),
    [productDescriptor, salesCampaignCode],
  );
  const isProductOnSale = productHelper.isProductOnSale();
  const packCode = productHelper.selectBestPackage()?.packCode ?? INVALID_PACKAGE_CODE;

  const salePerPack = data?.salePerPack;
  const salesData = salePerPack?.[packCode];

  const someSaleHasStock = Object.values(data?.salePerPack ?? {}).some((sale) => !sale.soldOut);
  const tagColour = someSaleHasStock ? "active" : "inactive";

  const stock =
    productDescriptor.availability?.packagingInformation.reduce((acc, pack) => getInStock(pack) + acc, 0) ?? 0;
  const stockLoading = productDescriptor.isAvailabilityLoading;

  const imgSize =
    pageSize <= useNumericScreenSize.desktopS || isTechnicalView ? PRODUCT_IMG_SIZE_SMALL : PRODUCT_IMG_SIZE_MEDIUM;

  const showKIA =
    pageType === SearchPageType.SEARCH &&
    window.SCHA.feature.zoeSRP &&
    isSiteRegion(CountryCode.GERMANY) &&
    CURRENT_LANGUAGE == "de" &&
    productDescriptor.base.url?.includes("rotary/waelz--und-gleitlager");
  const isKIAinRow = pageSize < useNumericScreenSize.tablet || pageSize >= useNumericScreenSize.desktopS;
  useScript("https://orca-cdn.zoovu.com/core-js/zoovu-tag-library/zoovu-tag-library-latest.min.js");

  return (
    <SRPPackageRestrictionContext productCode={productCode}>
      <SalesDataProvider value={data}>
        <ProductDescriptorContext value={productDescriptor}>
          <ItemContent isTechnicalView={isTechnicalView}>
            {isProductOnSale && <FloatingSaleTag variant={tagColour} />}
            <Box sx={{ justifySelf: "flex-start" }}>
              <Product.PdpLink>
                <Product.PrimaryImageWithLogosV2 size={imgSize} gap={8} />
              </Product.PdpLink>
            </Box>
            <Stack gap={2}>
              <Stack gap={0.5}>
                <Product.DesignationContainer>
                  <Product.PreferredIndicator />
                  <Product.PdpLink>
                    <Product.DesignationV2 variant="titleMedium" fontWeight={"bold"} />
                  </Product.PdpLink>
                </Product.DesignationContainer>
                <TagContainer>
                  <Product.MMDTag />
                  <ReconditionableTag />
                </TagContainer>
                <Product.ShortDescriptionV2 variant="bodySmall" color="text.secondary" />
              </Stack>

              {!isTechnicalView ? (
                <Stack gap={{ phone: 2, "desktop-m": 3 }} direction={{ phone: "column", "desktop-m": "row" }}>
                  <ProductFeatures unitMode={unitMode} />
                  <ProductParameters />
                </Stack>
              ) : (
                <ProductParameters />
              )}

              <Stack
                gap={1}
                alignItems={isKIAinRow ? "center" : "flex-start"}
                flexDirection={isKIAinRow ? "row" : "column"}
              >
                {isProductComparisonCheckboxVisible(productDescriptor.base) && (
                  <ProductComparisonCheckbox productCode={productCode} gaLocation={gaLocation} />
                )}
                {showKIA && (
                  <>
                    <ZoovuZoe ref={zoovuRef} display="popup" region="eu" locale="de-DE" sku={productCode} />
                    <Button
                      variant="text"
                      startIcon={<AutoAwesomeOutlinedIcon />}
                      onClick={() => zoovuRef.current?.open()}
                    >
                      {t("zoovuZoe.zoe.button.text")}
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>
            {showHorizontalDivider && <Divider orientation={"horizontal"} flexItem />}
            {showVerticalDivider && <Divider orientation={"vertical"} flexItem />}

            <Stack gap={2}>
              {isProductOnSale && salesData && <StyledSaleEndIndicator endDate={salesData?.endDate} />}
              {canSeePrice && <PriceSection packCode={packCode} isTechnicalView={isTechnicalView} />}
              {!isProductOnSale && canSeeStock && !stockLoading && <StyledStockIndicator stock={stock} />}
              {!isProductOnSale && canSeeStock && stockLoading && <CircularProgress size={20} />}
              {isProductOnSale && canSeeStock && salesData && (
                <SaleQuantityIndicator.V2 quantity={salesData.salesStock} />
              )}
              <ButtonSection />
            </Stack>
          </ItemContent>
        </ProductDescriptorContext>
      </SalesDataProvider>
    </SRPPackageRestrictionContext>
  );
};

const StyledStockIndicator = styled(StockIndicator.V2)({
  alignSelf: "flex-start",
});

const StyledSaleEndIndicator = styled(SaleEndIndicator.V2)({
  alignSelf: "flex-start",
});

const ProductParameters = () => {
  const { t } = useTranslation();
  const descriptor = useProductDescriptorContext();
  const context = { t, product: descriptor };

  const data = [
    ProductParams2.SchaefflerId,
    ProductParams2.ProductAliases({ mode: "13-digits", visibleAliasesLayout: "inline" }),
  ]
    .map((param) => param(context))
    .filter(isNonNull);

  return <Parameters data={data} parameterVariant="row" />;
};

const ProductFeatures = ({ unitMode }: { unitMode: UnitMode }) => {
  const descriptor = useProductDescriptorContext();
  const data = getFeaturesAsParams(descriptor.base.features, unitMode);

  if (data.length === 0) {
    return null;
  }

  return (
    <ParameterStack parameterVariant={"row"}>
      {data.map((param, index) => (
        <Parameter
          key={index}
          label={param.label}
          value={param.value ?? "-"}
          slotProps={getSlotParams(param.converted)}
        />
      ))}
    </ParameterStack>
  );
};

function getSlotParams(isConverted: boolean) {
  if (!isConverted) {
    return undefined;
  }
  return {
    label: {
      color: "info",
    },
    value: {
      color: "info",
    },
  };
}

const ItemRoot = styled(Card)(({ theme }) => ({}));

const ItemContent = styled(CardContent, {
  shouldForwardProp: (prop) => prop !== "isTechnicalView",
})<{ isTechnicalView?: boolean }>(({ theme, isTechnicalView }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  position: "relative",

  [theme.breakpoints.up("tablet")]: {
    display: "grid",
    gridTemplateColumns: isTechnicalView
      ? `${PRODUCT_LOGO_SIZE + PRODUCT_IMG_SIZE_SMALL}px 270px 1px 222px`
      : `${PRODUCT_LOGO_SIZE + PRODUCT_IMG_SIZE_SMALL}px 3fr auto 2fr`,
  },

  [theme.breakpoints.up("desktop-m")]: {
    display: "grid",
    gridTemplateColumns: isTechnicalView
      ? `${PRODUCT_LOGO_SIZE + PRODUCT_IMG_SIZE_SMALL}px 270px 1px 222px`
      : `${PRODUCT_LOGO_SIZE + PRODUCT_IMG_SIZE_MEDIUM}px 3fr auto 2fr`,
  },
}));

const PRODUCT_LOGO_SIZE = 8 /* gap */ + 32; /* logo */
const PRODUCT_IMG_SIZE_SMALL = 96;
const PRODUCT_IMG_SIZE_MEDIUM = 156;

const FloatingSaleTag = styled(SaleTag)({
  position: "absolute",
  top: "0",
  left: "0",
});

export const PriceSection = ({ packCode, isTechnicalView }: { packCode: string; isTechnicalView?: boolean }) => {
  const { t } = useTranslation();
  const data = useSalesData();
  const productDescriptor = useProductDescriptorContext();

  const salesData = data?.salePerPack?.[packCode];

  const { price, oldPrice, netPrice, discountFormatted } = useSelectPricingDetails({
    packCode,
    quantity: 1,
    priceReference: productDescriptor.priceReference,
    productCode: productDescriptor.base.code,
    salesData,
    saleActivated: true,
  });

  const isOnSale = !!salesData;
  const listPrice = getListPrice({ product: productDescriptor, packCode });

  return (
    <Stack gap={2}>
      <Box>
        <Stack direction="row" gap={1} alignItems={"center"}>
          <CurrentPrice price={price} zeroText={t("product.priceOnRequest")} variant="headlineSmall" />
          {isOnSale && <SaleDiscountIndicator.V2 discountFormatted={discountFormatted} variant="titleLarge" />}
        </Stack>
        {isOnSale && <OldPrice price={oldPrice} />}
        <PriceV3.NetPriceUnit price={netPrice} className={"label-medium"} />
        {!isOnSale && !isTechnicalView && <PriceV3.ExtraLabels price={netPrice} />}
      </Box>
      {!isOnSale && listPrice && !!listPrice.value && !isTechnicalView && (
        <Box>
          <PriceElements.Price price={listPrice} variant="titleMedium" fontWeight={"bold"} />
          <PriceElements.ListPriceLabel unit={netPrice?.unit ?? 1} variant="labelMedium" />
        </Box>
      )}
    </Stack>
  );
};

const CurrentPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  ...getFontStyle(FontSize.BodySmall),
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));

const ButtonSection = () => {
  const productDescriptor = useProductDescriptorContext();
  const priceReference = productDescriptor.priceReference;
  const purchasePlace = usePurchasePlace();

  const pageSize = useNumericScreenSize();
  const useSideButtons =
    productDescriptor.base.externalBrand || productIsLinear(productDescriptor)
      ? false
      : pageSize < useNumericScreenSize.tablet || userIsCustomer();

  const [openAddToWishlist, addToWishlistModal] = useAddProductToWishlistFlow({
    productInput: productDescriptor.base,
    initialQuantity: 1,
    initialPriceReference: productDescriptor.priceReference,
  });

  const onWishlistBtnClick = () => {
    openAddToWishlist();
  };

  const doAddToCart = async () => {
    await ProductService.getProductDescriptor(productDescriptor.base.code, priceReference, { fetch: true }).promise;

    await AddToCartService.openAddToCartWizard(
      {
        productCode: productDescriptor.base.code,
        restrictionQuery: "",
        source: "srp",
      },
      { page: purchasePlace, recommendationPlace: purchasePlace },
    );
  };

  const webservicesDisabled = window.SCHA.config.disableAllWS;
  const addToCartDisabled = webservicesDisabled;

  const buttons = renderButtonsNew(
    productDescriptor,
    {
      atc: {
        onClick: doAddToCart,
        disabled: addToCartDisabled,
      },
      wishlist: {
        onClick: onWishlistBtnClick,
      },
    },
    {
      sideButton: useSideButtons,
      maxButtons: 2,
    },
  );

  return (
    <>
      {addToWishlistModal}
      {useSideButtons && (
        <Stack direction="row" gap={2}>
          {buttons.buttons[0]}
          {buttons.sideButtons[0]}
        </Stack>
      )}
      {!useSideButtons && <Stack gap={1}>{buttons.buttons}</Stack>}
    </>
  );
};

const NewVersionMessageSearch = ({
  inStock,
  successorProduct,
}: {
  inStock: boolean;
  successorProduct: ProductData;
}) => {
  let color;
  let bgColor;

  if (inStock) {
    color = C_ON_SURFACE;
    bgColor = C_SURFACE;
  } else {
    color = C_ON_INFORMATION_CONTAINER;
    bgColor = C_INFORMATION_CONTAINER;
  }

  return (
    <ProductContext value={successorProduct}>
      <Paper
        sx={{
          px: 2,
          py: 1,
          backgroundColor: bgColor,
          color: color,
          borderRadius: 0,
        }}
      >
        <Typography variant="bodySmall">
          <Trans i18nKey={"product.hasNewerVersion"}>
            This product has a new version:{" "}
            <Product.PdpLink variant={LinkType.INHERIT} className="f-bold">
              {{ name: successorProduct.designation }}
            </Product.PdpLink>
          </Trans>
        </Typography>
      </Paper>
    </ProductContext>
  );
};

export const TagContainer = styled("div")({
  display: "flex",
  gap: 8,
  ["&:empty"]: {
    display: "none",
  },
});
